import { useEffect, useState } from "react";
import { useApi } from "../../Helpers/ApiHook";
import { useSearchParams } from "react-router-dom";

const CitiesFilterGroup = ({
    selectedStateIds,
    selectedCitiesIds,
    setSelectedCitiesIds
}) => {

    const { get } = useApi();
    const [searchParams, setSearchParams] = useSearchParams();

    const [isCitiesOpen, setIsCitiesOpen] = useState(true);

    const [listData, setListData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    // 4. Data Fetching and Consolidation Logic
    useEffect(() => {
        const fetchCities = async () => {
            if (selectedStateIds.length === 0) {
                setListData([]);
                return;
            }

            setIsLoading(true);
            const cityPromises = selectedStateIds.map(sId => get(`cities/${sId}`, {}));

            try {
                const results = await Promise.all(cityPromises);

                let combinedCities = [];
                results.forEach(res => {
                    if (res?.status && Array.isArray(res.data)) {
                        combinedCities = combinedCities.concat(res.data);
                    }
                });

                const uniqueCities = Array.from(new Map(combinedCities.map(item => [item.id, item])).values());

                setListData(uniqueCities);
            } catch (error) {
                console.error("Error fetching cities data:", error);
                setListData([]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchCities();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedStateIds, get]);

    // 5. URL Syncing for Cities
    useEffect(() => {
        setSearchParams((prev) => {
            const newParams = new URLSearchParams(prev);

            if (selectedCitiesIds && selectedCitiesIds.length > 0) {
                const paramValue = selectedCitiesIds.join(',');
                if (newParams.get('cities') !== paramValue) {
                    newParams.set('cities', paramValue);
                }
            } else {
                if (newParams.has('cities')) {
                    newParams.delete('cities');
                }
            }
            return newParams;
        }, { replace: true });
    }, [selectedCitiesIds, setSearchParams]);

    // 6. Checkbox Change Handler
    const handleCheckboxChange = (cityId) => {
        let newValue = [...selectedCitiesIds];
        const cityIdString = String(cityId);
        const isSelected = newValue.includes(cityIdString);

        if (isSelected) {
            newValue = newValue.filter((lid) => String(lid) !== cityIdString);
        } else {
            newValue.push(cityIdString);
        }

        setSelectedCitiesIds(Array.from(new Set(newValue)));
    };

    return (
        <div className="w-full">
            {/* Single Title for Cities */}
            <div
                className="w-full flex flex-row justify-between items-center cursor-pointer mt-5 mb-3"
                onClick={() => setIsCitiesOpen(!isCitiesOpen)}
            >
                <div className="text-white text-md font-medium mb-1">Cities</div>
                <div className={`transform transition-transform duration-300 text-2xl leading-none ${isCitiesOpen ? 'rotate-0' : 'rotate-180'}`}>
                    <img className="w-5 h-5 transition-transform duration-300" src="/assets/services/1/angle-down.png" alt="angle-down" />
                </div>
            </div>

            {/* Outer div for the open/close transition and fixed max height (max-h-48) */}
            <div
                className={`w-full flex flex-col justify-center items-start overflow-hidden transition-all duration-500 ease-in-out ${isCitiesOpen ? 'max-h-48 opacity-100' : 'max-h-0 opacity-0'}`}
            >
                <div
                    // Inner div enforces scrolling and applies scrollbar styling
                    // FIX: Removed h-full to allow content to overflow max-h-48 limit of the parent
                    className="w-full flex flex-col overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400/50 scrollbar-track-gray-700/30 hover:scrollbar-thumb-gray-400/70 space-y-2 py-1"
                    style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(156, 163, 175, 0.5) rgba(55, 65, 81, 0.3)' }}
                >
                    {isLoading && (
                        <div className="flex justify-center py-2">
                            <span className="text-[0.75rem] text-gray-400 ml-2">Loading cities...</span>
                        </div>
                    )}

                    {!isLoading && listData.length > 0 ? (
                        listData.map((item) => {
                            const isSelected = selectedCitiesIds.includes(String(item.id));

                            return (
                                <label key={item.id ?? String(item.name)} className="flex items-center text-gray-300 text-sm cursor-pointer hover:text-white">
                                    <input
                                        type="checkbox"
                                        className="form-checkbox h-[1rem] w-[1rem] text-blue-500 bg-gray-700 border-gray-600 rounded focus:ring-blue-500 transition duration-150"
                                        checked={!!isSelected}
                                        onChange={() => handleCheckboxChange(item.id)}
                                    />
                                    <span className="text-[0.75rem] ml-2 font-light">{item.name || '-'}</span>
                                </label>
                            );
                        })
                    ) : (
                        !isLoading && (
                            <div className="flex flex-col items-center py-2">
                                <span className="text-[0.75rem] text-gray-700 ml-2">
                                    {selectedStateIds.length === 0 ? "Select a state to see cities" : "No cities available"}
                                </span>
                            </div>
                        )
                    )}
                </div>
            </div>
        </div>
    );
};

export { CitiesFilterGroup };